import { parseISO } from 'date-fns';
import type { Reminder, Task, Time } from 'models';
import { type PeriodicityType, getNextPeriodDate } from 'models/activity';
import type { LastActivitySubmission } from 'models/service/activity';
import type {
  ActivityContentObjectDetailDataObject,
  ActivityContentObjectListDataObject,
  ContentObjectActivityDataObject,
  ProgramActivityContentObjectDetailDataObject,
  ProgramActivityContentObjectListDataObject,
} from '../../service';
import {
  type ProgramContentObject,
  type ProgramContentObjectItem,
  type ProgramContentObjectItemDetail,
  createProgramContentObject,
  createProgramContentObjectData,
  createProgramContentObjectDetailData,
} from '../contentable-type-content-objects';

export type ContentObjectActivity = {
  createdAt: Date;
  description?: string;
  endTime: Date;
  htmlDescription?: string;
  id: string;
  isWithings: boolean;
  lastActivitySubmission?: LastActivitySubmission;
  multipleSubmissions: boolean;
  name: string;
  nextPeriodDate?: Date;
  periodicityType: string;
  reminders?: Reminder[];
  startTime: Date;
  tasks: Task[];
  times?: Time[];
};

export const createContentObjectActivity = (data: ContentObjectActivityDataObject): ContentObjectActivity => ({
  createdAt: parseISO(data.created_at),
  description: data.description,
  endTime: parseISO(data.end_time),
  htmlDescription: data.html_description,
  id: data.id,
  isWithings: data.is_withings,
  lastActivitySubmission: data.last_activity_submission,
  multipleSubmissions: data.multiple_submissions,
  name: data.name,
  nextPeriodDate:
    getNextPeriodDate(
      parseISO(data.start_time),
      parseISO(data.end_time),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Backend documents periodicity_type as a plain string
      data.periodicity_type as PeriodicityType,
    ) ?? undefined,
  periodicityType: data.periodicity_type,
  reminders: data.reminders,
  startTime: parseISO(data.start_time),
  tasks: data.tasks,
  times: data.times,
});

export type ActivityContentObjectDetail = ProgramActivityContentObjectDetail;

export type ActivityContentObjectList = ProgramActivityContentObjectList;

export type ProgramActivityContentObjectDetail = {
  activity: ContentObjectActivity;
  program: ProgramContentObjectItemDetail;
  type: 'activity';
} & Omit<ProgramContentObject, 'program'>;

export type ProgramActivityContentObjectList = {
  activity: ContentObjectActivity;
  program: ProgramContentObjectItem;
  type: 'activity';
} & Omit<ProgramContentObject, 'program'>;

export const createActivityContentObjectDetail = (
  data: ActivityContentObjectDetailDataObject,
): ActivityContentObjectDetail => createProgramActivityContentObjectDetail(data);

export const createActivityContentObjectList = (data: ActivityContentObjectListDataObject): ActivityContentObjectList =>
  createProgramActivityContentObjectList(data);

export const createProgramActivityContentObjectDetail = (
  data: ProgramActivityContentObjectDetailDataObject,
): ProgramActivityContentObjectDetail => ({
  ...createProgramContentObject(data),
  activity: createContentObjectActivity(data.activity),
  program: createProgramContentObjectDetailData(data.program),
  type: 'activity',
});

export const createProgramActivityContentObjectList = (
  data: ProgramActivityContentObjectListDataObject,
): ProgramActivityContentObjectList => ({
  ...createProgramContentObject(data),
  activity: createContentObjectActivity(data.activity),
  program: createProgramContentObjectData(data.program),
  type: 'activity',
});
